/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { shallowEqual, useSelector } from 'react-redux';
import { selectDistanceToBeamEdgeNmMajorThreshold, selectDistanceToBeamEdgeNmMinorThreshold } from '../../store';
import appTheme from '../common/theme/appTheme.json';

export interface HcxShortestDistanceToBeamEdgeDisplayProps {
  shortestDistanceToBeamEdge: number;
}

function HcxShortestDistanceToBeamEdgeDisplay({
  shortestDistanceToBeamEdge,
}: HcxShortestDistanceToBeamEdgeDisplayProps) {
  const [shortestDistanceBeamEdgeColor, setShortestDistanceBeamEdgeColor] = useState<string>(appTheme.primaryTextColor);
  const distanceToBeamEdgeNmMinorThreshold = useSelector(selectDistanceToBeamEdgeNmMinorThreshold, shallowEqual);
  const distanceToBeamEdgeNmMajorThreshold = useSelector(selectDistanceToBeamEdgeNmMajorThreshold, shallowEqual);
  const tooltipInheritStyling = { color: 'inherit', fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit' };

  // Change the color of the shortest distance to beam edge metric based on the thresholds
  useEffect(() => {
    let distanceColor = appTheme.primaryTextColor; // Nominal

    if (
      shortestDistanceToBeamEdge <= distanceToBeamEdgeNmMinorThreshold &&
      shortestDistanceToBeamEdge > distanceToBeamEdgeNmMajorThreshold
    ) {
      distanceColor = appTheme.warningTextColor; // Sub-optimal
    } else if (shortestDistanceToBeamEdge <= distanceToBeamEdgeNmMajorThreshold) {
      distanceColor = appTheme.errorTextColor; // Critical
    }

    setShortestDistanceBeamEdgeColor(distanceColor);
  }, [shortestDistanceToBeamEdge, distanceToBeamEdgeNmMinorThreshold, distanceToBeamEdgeNmMajorThreshold]);

  return (
    <Box
      sx={{
        paddingLeft: '25px',
        height: '20px',
        marginBottom: '5px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400 }}>HCX Beam Edge:&nbsp;</Typography>
      <Typography sx={{ fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: shortestDistanceBeamEdgeColor }}>
        {shortestDistanceToBeamEdge} NM
      </Typography>
      <Tooltip
        title={
          <Box sx={{ flexDirection: 'column' }}>
            <Typography sx={tooltipInheritStyling}>
              Distance to closest edge of active HCX beam in nautical miles (NM).
            </Typography>
            <Typography sx={tooltipInheritStyling}>
              &nbsp;&nbsp;&nbsp;&gt; {distanceToBeamEdgeNmMinorThreshold} NM is normal.
            </Typography>
            <Typography sx={tooltipInheritStyling}>
              &nbsp;&nbsp;&nbsp;&le; {distanceToBeamEdgeNmMinorThreshold} NM is{' '}
              <span style={{ color: appTheme.warningTextColor }}>sub-optimal</span>.
            </Typography>
            <Typography sx={tooltipInheritStyling}>
              &nbsp;&nbsp;&nbsp;&le; {distanceToBeamEdgeNmMajorThreshold} NM is{' '}
              <span style={{ color: appTheme.errorTextColor }}>critical</span>.
            </Typography>
          </Box>
        }
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#465967', marginLeft: '5px' }} />
      </Tooltip>
    </Box>
  );
}

export default HcxShortestDistanceToBeamEdgeDisplay;
